import React, { useEffect, useState } from 'react';
import { Icon } from 'assets/icons';
import GmModal from 'app/shared/modal/modal';
import { MultiSelect } from 'app/shared/select';
import { useLabelService } from 'hooks/api/agents/label';
import { useLabelAssignmentService } from 'hooks/api/agents/label-assignment';
import { useLeadActivityService } from 'hooks/api/agents/lead/activity';
import styles from './labels.module.css';

export const ResourceLabels = ({ resource, show_picker = false, onClosePicker = () => {} }) => {
  const { fetchLabels, createLabel } = useLabelService();
  const { fetchLabelAssignments, createLabelAssignment, deleteLabelAssignment } =
    useLabelAssignmentService();
  const { createActivity } = useLeadActivityService();

  const [labels, setLabels] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const resource_key = resource ? `${resource.kind}:${resource.id}` : null;

  useEffect(() => {
    fetchLabels({ query_string: 'population=200' }).then(({ labels: fetched }) =>
      setLabels(fetched || [])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resource_key) return;
    setLoading(true);
    fetchLabelAssignments({
      query_string: `resource.kind=${resource.kind}&resource.id=${resource.id}&population=100`
    })
      .then(({ label_assignments }) => setAssignments(label_assignments || []))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resource_key]);

  const assigned_ids = new Set(assignments.map((a) => a.label_id));
  const label_options = labels
    .filter((l) => !assigned_ids.has(l.id))
    .map((l) => ({ value: l.id, label: l.name }));

  const handleAddLabel = async (option) => {
    if (!option || !resource) return;
    setSaving(true);

    let label_id = option.value;
    if (option.__isNew__) {
      const { label } = await createLabel({ name: option.label.trim() });
      if (!label?.id) {
        setSaving(false);
        return;
      }
      label_id = label.id;
      setLabels((prev) => [...prev, label]);
    }

    const { label_assignment } = await createLabelAssignment({ label_id, resource });
    setSaving(false);
    if (label_assignment) {
      setAssignments((prev) => [...prev, label_assignment]);
      await createActivity({
        subject: resource,
        outcome: 'neutral',
        body: `Added label "${option.label}"`
      });
    }
    onClosePicker();
  };

  const handleRemoveLabel = async (assignment) => {
    const removed = await deleteLabelAssignment(assignment.id);
    if (removed) {
      setAssignments((prev) => prev.filter((a) => a.id !== assignment.id));
    }
  };

  if (!resource) return null;

  return (
    <>
      {!loading && assignments.length > 0 && (
        <div className={styles.labelsRow}>
          {assignments.map((assignment) => {
            const label = labels.find((l) => l.id === assignment.label_id);
            if (!label) return null;
            return (
              <span
                key={assignment.id}
                className={styles.labelChip}
                style={label.color ? { borderColor: label.color, color: label.color } : undefined}
              >
                {label.name}
                <Icon
                  name="close"
                  width={10}
                  height={10}
                  onClick={() => handleRemoveLabel(assignment)}
                />
              </span>
            );
          })}
        </div>
      )}

      <GmModal
        show_header
        show_title
        title="Add Label"
        show_modal={show_picker}
        bodyClassName={styles.labelModal}
        onClose={onClosePicker}
      >
        <MultiSelect
          options={label_options}
          value={null}
          onChange={handleAddLabel}
          isMulti={false}
          isClearable={false}
          isCreatable
          placeholder="Search or create a label…"
          menuPortalTarget={document.body}
          disabled={saving}
        />
      </GmModal>
    </>
  );
};

export default ResourceLabels;
